import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const OperationalPreferencesSection = ({ 
  formData, 
  handleInputChange, 
  handleCheckboxChange,
  errors 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const airlineOptions = [
    { value: 'AA', label: 'American Airlines' },
    { value: 'DL', label: 'Delta Air Lines' },
    { value: 'UA', label: 'United Airlines' },
    { value: 'WN', label: 'Southwest Airlines' },
    { value: 'B6', label: 'JetBlue Airways' },
    { value: 'AS', label: 'Alaska Airlines' }
  ];

  return (
    <div className="card">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-green/20 glow-subtle">
            <Icon name="Settings" size={20} color="var(--color-accent-green)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">Operational Preferences</h2>
            <p className="text-xs md:text-sm text-muted-foreground font-caption">
              Customize operational alerts and commute settings
            </p>
          </div>
        </div>
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          size={24} 
          color="var(--color-muted-foreground)" 
        />
      </div>

      {isExpanded && (
        <div className="mt-6 md:mt-8 space-y-6 md:space-y-8">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Connection & Rest Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <Input
                label="Minimum Connection Time (minutes)"
                type="number"
                name="minimumConnectionTime"
                value={formData?.minimumConnectionTime}
                onChange={handleInputChange}
                placeholder="45"
                min="30"
                max="120"
                error={errors?.minimumConnectionTime}
                description="Alert threshold for tight connections"
                className="w-full font-data"
              />

              <Input
                label="Preferred Rest Period (hours)"
                type="number"
                name="preferredRestPeriod"
                value={formData?.preferredRestPeriod}
                onChange={handleInputChange}
                placeholder="12"
                min="10"
                max="24"
                step="0.5"
                error={errors?.preferredRestPeriod}
                description="Your preferred minimum rest vs FAR 117 legal minimum"
                className="w-full font-data"
              />
            </div>
          </div>

          <div className="h-px bg-accent-green/20"></div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base md:text-lg font-semibold text-foreground">Commute Preferences</h3>
              <Checkbox
                label="I commute to my base"
                checked={formData?.isCommuter}
                onChange={(e) => handleCheckboxChange('isCommuter', e?.target?.checked)}
              />
            </div>

            {formData?.isCommuter && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <Input
                    label="Maximum Commute Time (hours)"
                    type="number"
                    name="maxCommuteTime"
                    value={formData?.maxCommuteTime}
                    onChange={handleInputChange}
                    placeholder="3"
                    min="1"
                    max="12"
                    step="0.5"
                    className="w-full font-data"
                  />

                  <Input
                    label="Preferred Commute Buffer (minutes)"
                    type="number"
                    name="commuteBuffer"
                    value={formData?.commuteBuffer}
                    onChange={handleInputChange}
                    placeholder="90"
                    min="30"
                    max="240"
                    description="How early to arrive before duty starts"
                    className="w-full font-data"
                  />
                </div>

                <Select
                  label="Preferred Airlines"
                  options={airlineOptions}
                  value={formData?.preferredAirlines}
                  onChange={(value) => handleInputChange({ target: { name: 'preferredAirlines', value } })}
                  placeholder="Select preferred airlines"
                  multiple
                  searchable
                  className="w-full"
                />

                <Checkbox
                  label="Enable backup flight alerts"
                  description="Get notified if primary commute is risky"
                  checked={formData?.backupFlightAlerts}
                  onChange={(e) => handleCheckboxChange('backupFlightAlerts', e?.target?.checked)}
                />
              </div>
            )}
          </div>

          <div className="h-px bg-accent-green/20"></div>

          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Duty Day Alerts</h3>
            <div className="space-y-3">
              <Checkbox
                label="Alert when FDP approaches 80% of limit"
                checked={formData?.alertFdp80}
                onChange={(e) => handleCheckboxChange('alertFdp80', e?.target?.checked)}
              />
              <Checkbox
                label="Alert when FDP approaches 90% of limit"
                checked={formData?.alertFdp90}
                onChange={(e) => handleCheckboxChange('alertFdp90', e?.target?.checked)}
              />
              <Checkbox
                label="Alert on any FAR 117 violation detected"
                checked={formData?.alertFar117Violation}
                onChange={(e) => handleCheckboxChange('alertFar117Violation', e?.target?.checked)}
              />
              <Checkbox
                label="Alert when flight delay causes rest violation"
                checked={formData?.alertRestViolation}
                onChange={(e) => handleCheckboxChange('alertRestViolation', e?.target?.checked)}
              />
              <Checkbox
                label="Alert on schedule change detected"
                checked={formData?.alertScheduleChange}
                onChange={(e) => handleCheckboxChange('alertScheduleChange', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationalPreferencesSection;